/**
 * The text field in front of a native <input type="date"> (DEVDV-683). The native input stays the form control
 * (name, data-nette-rules, min/max, toggles) and only ever receives a complete, real date or ''.
 */
import {EMPTY, INCOMPLETE, VALID, formatDate, localePattern, parseDate} from './dateParser.js';
import {createCalendar} from './calendarAdapter.js';

// Must match the sheet's media query in the picker's stylesheet.
const SHEET_QUERY = '(max-width: 520px), (max-height: 500px) and (pointer: coarse)';
// Phone panel: distance from the visible viewport's edges and from the field above it; the height it keeps while
// the page scrolls its field away.
const SHEET_EDGE       = 16;
const SHEET_GAP        = 8;
const SHEET_MIN_HEIGHT = 160;

const SVG_NS = 'http://www.w3.org/2000/svg';
const CALENDAR_ICON = 'M7 2v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V2h-2v2H9V2H7zm-2 7h14v11H5V9z';
const EDITED = 'data-date-proxy-edited';

/**
 * @param {HTMLInputElement} native
 * @param {{locale: string, texts: Object}} config the data-date-picker JSON rendered by DateControl
 */
export function enhanceDateInput(native, config) {
	new DateProxy(native, config);
}

class DateProxy {
	constructor(native, {locale, texts}) {
		if (!native.id) {
			throw new Error('A date picker needs a native input with an id');
		}

		native.setAttribute('data-date-picker-ready', '');

		this.native = native;
		this.locale = locale;
		this.texts = texts;
		this.pattern = localePattern(locale);
		this.placeholder = this.pattern.order.map((type) => texts.placeholder[type]).join(this.pattern.separator);
		this.tooltipMode = native.closest('smart-input[data-error-message-render=tooltip]') !== null;
		this.dispatching = false;
		this.messageShown = false;
		this.calendar = null;

		this.build();
		this.listen();
		this.renderFromNative();
	}

	build() {
		const native = this.native;
		const smartInput = native.closest('smart-input');

		this.wrapper = createElement('div', 'date-picker');
		this.proxy = this.buildProxy(smartInput === null);
		this.button = this.buildButton();
		this.popup = this.buildPopup();
		this.message = createElement('small', 'date-picker-message');
		this.message.id = `${native.id}-message`;
		this.message.hidden = true;
		this.message.setAttribute('aria-live', 'polite');

		if (native.closest('dialog')) {
			this.wrapper.classList.add('date-picker--inline');
		}

		this.wrapper.append(this.proxy, this.button, this.popup);

		if (smartInput === null) {
			// Host admin renders a bare {input}: the wrapper takes its place and the message follows the wrapper.
			native.replaceWith(this.wrapper);
			this.wrapper.prepend(native);
			this.wrapper.after(this.message);
			this.box = this.wrapper;
		}
		else {
			this.box = native.closest('smart-input-border') ?? native.parentElement;
			native.before(this.wrapper);
			this.box.after(this.message);
		}

		// Stays in form.elements and validatable, hidden only visually (not [hidden]).
		native.classList.add('date-picker-native');
		native.tabIndex = -1;
		native.setAttribute('aria-hidden', 'true');

		document.querySelectorAll(`label[for="${CSS.escape(native.id)}"]`).forEach((label) => {
			label.htmlFor = this.proxy.id;
			label.addEventListener('mousedown', (event) => this.onLabelMousedown(event));
		});
	}

	buildProxy(copyClass) {
		const native = this.native;
		const proxy = document.createElement('input');

		// Host admin: the text field looks like the input it stands for.
		proxy.className = copyClass ? native.className : '';
		proxy.classList.add('date-picker-proxy');
		proxy.id = `${native.id}-text`;
		proxy.type = 'text';
		proxy.inputMode = 'numeric';
		proxy.autocomplete = 'off';
		proxy.placeholder = this.placeholder;
		proxy.setAttribute('data-date-proxy', native.id);
		proxy.setAttribute('aria-describedby', `${native.id}-message`);

		if (native.required) {
			proxy.setAttribute('aria-required', 'true');
		}

		return proxy;
	}

	buildButton() {
		const button = createElement('button', 'date-picker-button');
		button.type = 'button';
		button.setAttribute('aria-label', this.texts.openCalendar);
		button.setAttribute('aria-haspopup', 'dialog');
		button.setAttribute('aria-expanded', 'false');
		button.setAttribute('aria-controls', `${this.native.id}-popup`);

		const icon = document.createElementNS(SVG_NS, 'svg');
		icon.setAttribute('viewBox', '0 0 24 24');
		icon.setAttribute('width', '20');
		icon.setAttribute('height', '20');
		icon.setAttribute('aria-hidden', 'true');

		const path = document.createElementNS(SVG_NS, 'path');
		path.setAttribute('fill', 'currentColor');
		path.setAttribute('d', CALENDAR_ICON);
		icon.append(path);
		button.append(icon);

		return button;
	}

	buildPopup() {
		const popup = createElement('div', 'date-picker-popup');
		popup.id = `${this.native.id}-popup`;
		popup.hidden = true;
		popup.setAttribute('role', 'dialog');
		popup.setAttribute('aria-label', this.texts.calendar);

		this.calendarHost = createElement('div', 'date-picker-calendar-host');
		this.clearButton = createElement('button', 'date-picker-clear');
		this.clearButton.type = 'button';
		this.clearButton.textContent = this.texts.clear;
		this.closeButton = createElement('button', 'date-picker-close');
		this.closeButton.type = 'button';
		this.closeButton.textContent = this.texts.close;

		const footer = createElement('div', 'date-picker-footer');
		footer.append(this.clearButton, this.closeButton);
		popup.append(this.calendarHost, footer);

		return popup;
	}

	listen() {
		this.onDocumentPointer = (event) => this.onPointerOutside(event);
		this.onDocumentKey     = (event) => this.onEscape(event);
		this.onViewportChange  = () => this.followViewport(true);
		this.onViewportScroll  = (event) => this.onScroll(event);
		this.contentObserver   = typeof ResizeObserver === 'function' ? new ResizeObserver(() => this.followViewport(true)) : null;

		this.proxy.addEventListener('input', () => this.onInput());
		this.proxy.addEventListener('keydown', (event) => this.onKeydown(event));
		this.proxy.addEventListener('focus', () => this.onProxyFocus());
		this.proxy.addEventListener('date-proxy:flush', () => this.flushClear());
		this.proxy.addEventListener('date-proxy:report', () => this.showMessage(true));
		this.wrapper.addEventListener('focusout', (event) => this.onFocusOut(event));
		// Safari does not focus a clicked button, so the text field would blur into nowhere before the click.
		this.button.addEventListener('mousedown', (event) => event.preventDefault());
		this.button.addEventListener('click', () => (this.popup.hidden ? this.open() : this.close(true)));
		this.closeButton.addEventListener('click', () => this.close(true));
		this.clearButton.addEventListener('click', () => this.clear());

		// netteForms focuses the first invalid control, which is the hidden native one (F4).
		this.native.addEventListener('focus', () => this.proxy.focus());

		// jQuery's .trigger('change') does not reach addEventListener() listeners (F1), jQuery's own .on() sees both.
		if (typeof window.jQuery === 'function') {
			window.jQuery(this.native).on('input change', () => {
				this.onNativeChange();
			});
			window.jQuery(this.proxy).on('change', (event) => this.onProxyChange(event));
			return;
		}

		['input', 'change'].forEach((type) => this.native.addEventListener(type, () => this.onNativeChange()));
	}

	// A change from outside (load, Nette.toggle); our own commit is not one.
	onNativeChange() {
		if (!this.dispatching) {
			this.renderFromNative();
		}
	}

	onInput() {
		this.proxy.setAttribute(EDITED, '');
		const result = this.evaluate();

		// An emptied field waits for the focus to leave (D11): select-all, Backspace, retype must not pass through ''
		// and fire the "blank" triggers in the middle of retyping.
		if (result.state === VALID) {
			this.commit(result.iso);
		}

		this.refreshMessage();
	}

	// While the field holds focus, its label's mousedown would blur it before the click refocuses it (D11).
	onLabelMousedown(event) {
		if (this.wrapper.contains(document.activeElement)) {
			event.preventDefault();
		}
	}

	onKeydown(event) {
		if (event.altKey && event.key === 'ArrowDown') {
			event.preventDefault();
			this.open();
		}
	}

	// Tapping the text field while the sheet is open means "I want to type".
	onProxyFocus() {
		if (!this.popup.hidden && window.matchMedia(SHEET_QUERY).matches) {
			this.close(false);
		}
	}

	onProxyChange(event) {
		// Only a scripted change; the browser's own change on blur is the user typing.
		if (!event.isTrigger) {
			return;
		}

		// A Nette toggle emptied the text field itself with jQuery's .val('').trigger('change').
		if (this.proxy.value.trim() === '') {
			this.proxy.removeAttribute(EDITED);
			this.evaluate();
			this.showMessage(false);
			return;
		}

		// A re-trigger over the rendered value is no edit (D20).
		if (this.isEdited() || this.proxy.value !== formatDate(this.native.value, this.pattern)) {
			this.onInput();
		}
	}

	onFocusOut(event) {
		const target = event.relatedTarget;

		if (target instanceof Node && this.wrapper.contains(target)) {
			return;
		}

		// No target while the popup is open: the calendar re-rendered the focused button, focus did not leave.
		if (!(target instanceof Node) && !this.popup.hidden) {
			return;
		}

		// The window lost focus (another tab or app): the text field keeps it and the retype goes on (D11).
		if (!(target instanceof Node) && !document.hasFocus()) {
			return;
		}

		this.leave();
	}

	// Closing is not leaving (a scroll gesture moves no focus, D11): focus stays in the component, where the ordinary
	// focusout notices when it really leaves.
	onPointerOutside(event) {
		if (this.wrapper.contains(event.target)) {
			return;
		}

		if (this.popup.contains(document.activeElement)) {
			this.button.focus({preventScroll: true});
		}

		this.close(false);
	}

	leave() {
		this.close(false);
		this.flushClear();
		this.showMessage(true);
	}

	onEscape(event) {
		if (event.key !== 'Escape') {
			return;
		}

		event.preventDefault();
		this.close(true);
	}

	isEdited() {
		return this.proxy.hasAttribute(EDITED);
	}

	/**
	 * Sets the text field's validity from what is typed; an untouched field is always valid (D20).
	 */
	evaluate() {
		const result = parseDate(this.proxy.value, this.pattern);
		this.proxy.setCustomValidity(this.isEdited() ? this.messageFor(result) : '');

		return result;
	}

	messageFor(result) {
		if (result.state === INCOMPLETE) {
			return result.reason === 'year4' ? this.texts.year4 : this.texts.incomplete;
		}

		if (result.state === EMPTY) {
			return '';
		}

		if (result.state !== VALID) {
			return result.reason === 'range' || result.reason === 'impossible'
				? this.texts.invalid
				: this.texts.format.replaceAll('%format%', this.placeholder);
		}

		if (this.native.min && result.iso < this.native.min) {
			return this.ruleMessage('validateDateMin');
		}

		if (this.native.max && result.iso > this.native.max) {
			return this.ruleMessage('validateDateMax');
		}

		return '';
	}

	/**
	 * The server's own text for a bound; a bound without its rule is left to the server.
	 */
	ruleMessage(validator) {
		const rules = JSON.parse(this.native.getAttribute('data-nette-rules') ?? '[]');
		const rule = rules.find((item) => typeof item.op === 'string' && item.op.endsWith(validator));

		return rule?.msg ?? '';
	}

	/**
	 * @param {boolean} show false hides the message; true shows the current one, if any
	 */
	showMessage(show) {
		this.messageShown = show && !this.tooltipMode && this.proxy.validationMessage !== '';
		this.message.textContent = this.messageShown ? this.proxy.validationMessage : '';
		this.message.hidden = !this.messageShown;
		this.wrapper.classList.toggle('date-picker--invalid', this.messageShown);

		if (this.messageShown) {
			this.proxy.setAttribute('aria-invalid', 'true');
			return;
		}

		this.proxy.removeAttribute('aria-invalid');
	}

	// Messages appear on blur or a blocked submit, never per keystroke; once shown they follow the typing.
	refreshMessage() {
		if (this.messageShown) {
			this.showMessage(true);
		}
	}

	commit(iso) {
		if (this.native.value === iso) {
			return;
		}

		this.native.value = iso;
		this.dispatching = true;

		try {
			this.native.dispatchEvent(new Event('input', {bubbles: true}));
			this.native.dispatchEvent(new Event('change', {bubbles: true}));
		}
		finally {
			this.dispatching = false;
		}
	}

	flushClear() {
		if (this.isEdited() && parseDate(this.proxy.value, this.pattern).state === EMPTY) {
			this.commit('');
		}
	}

	renderFromNative() {
		this.proxy.value = formatDate(this.native.value, this.pattern);
		this.proxy.disabled = this.native.disabled;
		this.button.disabled = this.native.disabled;
		this.proxy.removeAttribute(EDITED);
		this.evaluate();
		this.showMessage(false);

		if (this.native.disabled) {
			this.close(false);
		}
	}

	open() {
		if (!this.popup.hidden || this.native.disabled) {
			return;
		}

		this.popup.hidden = false;
		this.button.setAttribute('aria-expanded', 'true');
		window.visualViewport?.addEventListener('resize', this.onViewportChange);
		window.visualViewport?.addEventListener('scroll', this.onViewportScroll);
		window.addEventListener('orientationchange', this.onViewportChange);
		window.addEventListener('resize', this.onViewportChange);
		// Capture: in the tenant admin the body, not the window, is the scroller.
		document.addEventListener('scroll', this.onViewportScroll, {capture: true, passive: true});

		// Shows the current value or today's month; opening never selects or writes a date.
		try {
			this.calendar = createCalendar(this.calendarHost, {
				value:        this.native.value,
				min:          this.native.min,
				max:          this.native.max,
				locale:       this.locale,
				firstWeekday: firstWeekday(this.locale),
				texts:        this.texts,
				onSelect:     (iso) => this.select(iso),
			});
		}
		catch (error) {
			// Do not leave a half-open popup without its document listeners.
			this.close(false);
			throw error;
		}

		// Placed once the calendar has its height; it changes again when the month or year grid is shown.
		this.followViewport(true);
		this.contentObserver?.observe(this.calendarHost);
		document.addEventListener('pointerdown', this.onDocumentPointer, {capture: true});
		document.addEventListener('keydown', this.onDocumentKey, {capture: true});
		setTimeout(() => this.calendar?.focus(), 0);
	}

	close(returnFocus) {
		if (this.popup.hidden) {
			return;
		}

		// Focus must leave the calendar before it is destroyed: removing the focused button blurs it, and that
		// focusout would read as leaving the component (an emptied text would be committed).
		if (returnFocus) {
			this.button.focus();
		}

		this.popup.hidden = true;
		this.button.setAttribute('aria-expanded', 'false');
		window.visualViewport?.removeEventListener('resize', this.onViewportChange);
		window.visualViewport?.removeEventListener('scroll', this.onViewportScroll);
		window.removeEventListener('orientationchange', this.onViewportChange);
		window.removeEventListener('resize', this.onViewportChange);
		document.removeEventListener('scroll', this.onViewportScroll, {capture: true});
		this.contentObserver?.disconnect();
		document.removeEventListener('pointerdown', this.onDocumentPointer, {capture: true});
		document.removeEventListener('keydown', this.onDocumentKey, {capture: true});
		this.calendar?.destroy();
		this.calendar = null;
		this.calendarHost.replaceChildren();
	}

	select(iso) {
		// The calendar's value goes through the same parser as typed text.
		const result = parseDate(iso, this.pattern);

		if (result.state !== VALID) {
			throw new Error(`The calendar produced an invalid date: ${iso}`);
		}

		this.proxy.value = formatDate(result.iso, this.pattern);
		this.proxy.setAttribute(EDITED, '');
		this.commit(result.iso);
		this.evaluate();
		this.refreshMessage();
		this.proxy.focus();
		this.close(false);
	}

	clear() {
		this.proxy.value = '';
		this.proxy.setAttribute(EDITED, '');
		this.commit('');
		this.evaluate();
		this.refreshMessage();
		this.proxy.focus();
		this.close(false);
	}

	// The panel's own scroll is the user reading it, not a reason to place it again.
	onScroll(event) {
		if (event.target instanceof Node && this.popup.contains(event.target)) {
			return;
		}

		this.followViewport(false);
	}

	/**
	 * @param {boolean} allowScroll false while the page scrolls: the panel follows its field, the page is not moved
	 */
	followViewport(allowScroll) {
		if (this.popup.hidden) {
			return;
		}

		const sheet = window.matchMedia(SHEET_QUERY).matches && !this.wrapper.classList.contains('date-picker--inline');

		// Clearing the max-height of a placed panel would clamp its inner scroll position.
		if (sheet) {
			this.placeSheet(allowScroll);
		}
		else {
			this.popup.style.top       = '';
			this.popup.style.maxHeight = '';
		}

		this.keepInsideViewport(sheet);
	}

	// Phone: a panel 16 px from the sides directly under the field, inside the visible viewport (on-screen
	// keyboard, rotation), so the field stays visible with it (DEVDV-685). When the panel does not fit under the
	// field, the field is scrolled up first; what still does not fit scrolls inside the panel.
	placeSheet(allowScroll) {
		const viewport = window.visualViewport;
		const bottom   = (viewport ? viewport.offsetTop + viewport.height : window.innerHeight) - SHEET_EDGE;
		const height   = this.popup.scrollHeight + this.popup.offsetHeight - this.popup.clientHeight;
		// A pinch-zoom resizes the visual viewport too; scrolling then would pull the zoomed view back to the field.
		const zoomed = (viewport?.scale ?? 1) > 1;
		let top      = this.box.getBoundingClientRect().bottom + SHEET_GAP;

		if (allowScroll && !zoomed && top + height > bottom) {
			this.box.scrollIntoView({block: 'start'});
			top = this.box.getBoundingClientRect().bottom + SHEET_GAP;
		}

		// Placed, it fits what is left of the screen; while the page scrolls its field away, it goes along with it.
		const available = bottom - top;
		const maxHeight = allowScroll ? Math.max(available, 0) : Math.max(available, SHEET_MIN_HEIGHT);

		this.popup.style.top       = `${Math.round(top)}px`;
		this.popup.style.maxHeight = `${Math.round(maxHeight)}px`;
	}

	// Desktop: a popup that would overflow the right edge moves left, still under the field (DEVDV-685).
	keepInsideViewport(sheet) {
		this.popup.style.left = '';

		if (sheet || this.wrapper.classList.contains('date-picker--inline')) {
			return;
		}

		// Where body is the scroller (tenant admin), its scrollbar sits inside the html box.
		const right = Math.min(document.documentElement.clientWidth, document.body.clientWidth) - 16;
		const rect = this.popup.getBoundingClientRect();
		const overflow = Math.min(rect.right - right, rect.left - 16);

		if (overflow > 0) {
			this.popup.style.left = `${-overflow}px`;
		}
	}
}

function createElement(tag, className) {
	const element = document.createElement(tag);
	element.className = className;

	return element;
}

/**
 * @returns {number} 1 = Monday … 7 = Sunday; Monday where the browser has no week info
 */
function firstWeekday(locale) {
	const intlLocale = new Intl.Locale(locale);
	const weekInfo = typeof intlLocale.getWeekInfo === 'function' ? intlLocale.getWeekInfo() : intlLocale.weekInfo;

	return weekInfo?.firstDay ?? 1;
}
